import React from 'react';
import { Link } from 'react-router-dom';
import {
  CheckCircle, Shield, Zap, FileText, Clock, TrendingUp, Users, Briefcase,
  Home, Heart, Gift, RefreshCw, ArrowUpCircle, Lightbulb, Star, Phone, Mail,
  MapPin, AlertTriangle, Headphones, Building, DollarSign, Percent, Calendar,
  CreditCard, Smartphone, Award, ThumbsUp, Handshake, BookOpen, Globe, Lock,
  FileCheck, ClipboardList
} from 'lucide-react';

const PersonalLoanBallia = () => {
  return (
    <div className="bg-gray-50">
      {/* Hero Banner */}
      <div className="relative bg-gradient-to-r from-green-800 to-green-700 text-white py-12 md:py-16 overflow-hidden">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            <div className="text-center lg:text-left">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-4 animate-fade-in-up">
                Personal Loan in Ballia
              </h1>
              <p className="text-lg md:text-xl mb-6 animate-fade-in-up animation-delay-200">
                Quick funds for your dreams – weddings, travel, medical emergencies, or any personal need.
              </p>
              <div className="flex flex-wrap gap-4 justify-center lg:justify-start animate-fade-in-up animation-delay-400">
                <button className="bg-yellow-400 hover:bg-yellow-300 text-gray-800 font-semibold px-6 py-2 rounded-full transition-all duration-300 transform hover:scale-105 shadow-lg">
                  Apply Now
                </button>
                <button className="bg-white/20 backdrop-blur-sm hover:bg-white/30 text-white font-semibold px-6 py-2 rounded-full transition-all duration-300">
                  Check Eligibility
                </button>
              </div>
            </div>
            <div className="flex justify-center lg:justify-end">
              <img
                src="https://images.unsplash.com/photo-1579621970795-87facc2f976d?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80"
                alt="Personal Loan"
                className="max-w-full h-auto rounded-2xl shadow-xl transition-transform duration-500 hover:scale-105"
              />
            </div>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 w-full overflow-hidden leading-none">
          <svg viewBox="0 0 1200 120" preserveAspectRatio="none" className="relative block w-full h-12">
            <path d="M321.39,56.44c58-10.79,114.16-30.13,172-41.86,82.39-16.72,168.19-17.73,250.45-.39C823.78,31,906.67,72,985.66,92.83c70.05,18.48,146.53,26.09,214.34,3V0H0V27.35A600.21,600.21,0,0,0,321.39,56.44Z" fill="white"></path>
          </svg>
        </div>
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* What is a Personal Loan? */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-green-700 mb-6 text-center">What is a Personal Loan?</h2>
          <div className="bg-white rounded-xl shadow-md p-8">
            <p className="text-gray-700 leading-relaxed">
              A personal loan is typically smaller than that of a mortgage. Business or commercial loans are not personal loans. Individuals can acquire loans for various reasons such as going on vacation, getting married, buying new furniture, etc.
            </p>
          </div>
        </div>

        {/* Features */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-green-700 mb-8 text-center">Features of a Personal Loan</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: Shield, text: "Unsecured loan – no collateral needed" },
              { icon: CheckCircle, text: "No missed credit card payments in last 6 months" },
              { icon: TrendingUp, text: "Interest rates based on salary, credit amount, history" },
              { icon: Clock, text: "Repayment tenure: 1-5 years" },
              { icon: FileText, text: "Minimal documentation, approval within 48 hours" },
              { icon: AlertTriangle, text: "Includes late payment penalties, pre-payment fees, etc." }
            ].map((item, idx) => (
              <div key={idx} className="bg-white p-6 rounded-xl shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                <item.icon className="w-10 h-10 text-green-600 mb-4" />
                <p className="text-gray-700">{item.text}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Balance Transfers */}
        <div className="mb-16 bg-white rounded-xl shadow-md p-8">
          <h2 className="text-3xl font-bold text-green-700 mb-6 text-center">Balance Transfers</h2>
          <p className="text-gray-700 mb-4">
            You transfer a loan balance from one lender to another. Paying off an existing loan requires a new loan. As a result of the transfer, you owe the same amount as before – you just owe another entity.
          </p>
          <p className="text-gray-700 mb-4">
            A balance transfer is a method of moving an outstanding loan amount from one lender to another.
          </p>
          <h3 className="text-xl font-semibold text-gray-800 mb-3">Reasons for doing a balance transfer:</h3>
          <ul className="list-disc list-inside space-y-2 text-gray-700">
            <li><strong>Better interest rate:</strong> Move your outstanding loan to a lender offering lower rates.</li>
            <li><strong>Lender offers:</strong> Many lenders offer balance transfer deals with 2-3% lower rates.</li>
            <li><strong>Service issues:</strong> Switch to a lender with better customer service.</li>
            <li><strong>Loan top‑ups:</strong> Borrow additional funds through a balance transfer with a top‑up loan.</li>
          </ul>
        </div>

        {/* How we can help */}
        <div className="mb-16 bg-gradient-to-r from-green-50 to-green-50 rounded-xl p-8">
          <h2 className="text-3xl font-bold text-green-700 mb-6 text-center">What Can We Do For You?</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              "We provide an unbiased platform to compare personal loan offers.",
              "We offer a customized loan consulting experience.",
              "Our experts help you determine which loan is right for you.",
              "We ensure your documents are in order.",
              "We make the process smooth, efficient, and easy.",
              "We handle any issues that arise during the application."
            ].map((item, idx) => (
              <div key={idx} className="flex items-start gap-3">
                <Lightbulb className="w-6 h-6 text-green-600 mt-1 flex-shrink-0" />
                <span className="text-gray-700">{item}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Why Choose CashPro */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-green-700 mb-8 text-center">Why Choose  CashPro Financial Services?</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              "Get best offers & deals from our partner banks & NBFCs.",
              "Compare personal loan offers and services – all in one place.",
              "Apply with the bank or NBFC of your choice, completely free of charge.",
              "Lowest rate of interest in the market, guaranteed.",
              "Hassle‑free process and easy documentation.",
              "Free document pickup by CashPro Financial Services team.",
              "Free expert guidance & advice from our dedicated team.",
              "We are not an agent of any bank – you have full control over your choice.",
              "No fees for our services.",
              "We understand you are in immediate need of money; we ensure quick processing and disbursal."
            ].map((item, idx) => (
              <div key={idx} className="flex items-start gap-3 bg-white p-4 rounded-lg shadow-sm hover:shadow-md transition-all">
                <CheckCircle className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
                <span className="text-gray-700">{item}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Documents Required */}
        <div className="mb-16 bg-white rounded-xl shadow-md p-8">
          <h2 className="text-3xl font-bold text-green-700 mb-6 text-center">Documents Required</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              "Identity Proof (Passport / PAN / Voter ID / Aadhaar / Driving License)",
              "Birth Certificate / Age Proof",
              "Address Proof (Aadhaar / Passport / Utility bills / Rent agreement)",
              "Business Address Proof (for self-employed)",
              "Income Proof: Salary slips (last 3 months), Bank statements, Form 16, ITR"
            ].map((item, idx) => (
              <div key={idx} className="flex items-start gap-3">
                <FileText className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
                <span className="text-gray-700">{item}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Eligibility Criteria */}
        <div className="mb-16 bg-white rounded-xl shadow-md p-8">
          <h2 className="text-3xl font-bold text-green-700 mb-6 text-center">Personal Loan Eligibility Criteria</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              "Indian citizen with regular source of income (salaried, self-employed, or business owner).",
              "Minimum age: 21 years; maximum age at loan maturity: 60 years.",
              "Stable employment: Minimum 1 year in current job (salaried) or 3 years business vintage.",
              "Credit score preferably 750 or above.",
              "Sufficient income to repay the loan (as per lender's criteria)."
            ].map((item, idx) => (
              <div key={idx} className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
                <span className="text-gray-700">{item}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Points to Consider */}
        <div className="mb-16 bg-yellow-50 border-l-4 border-yellow-500 p-6 rounded-r-xl">
          <div className="flex items-center gap-2 mb-4">
            <AlertTriangle className="w-6 h-6 text-yellow-600" />
            <h2 className="text-2xl font-bold text-gray-800">Considerations Before Taking a Personal Loan</h2>
          </div>
          <ul className="space-y-2 text-gray-700">
            <li>• Fill out the application form with all accurate information. The lender will process your loan based on your information.</li>
            <li>• Processing fees are deducted from the loan amount; do not pay any fees in cash/cheque to anyone for loan sanctioning.</li>
            <li>• Compare loan offers and deals, but hand over documents only to the lender of your choice.</li>
          </ul>
        </div>

        {/* Help Section */}
        <div className="bg-white rounded-2xl p-8 shadow-lg text-center">
          <h2 className="text-3xl font-bold text-green-700 mb-4">We Are Here to Help You</h2>
          <p className="text-gray-600 mb-6">Have questions? Our personal loan experts are ready to assist you.</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            <div>
              <p className="text-sm font-semibold text-gray-500 uppercase tracking-wide">APPLY FOR LOAN</p>
              <p className="text-gray-800 font-medium mt-1">Ready to get funds?</p>
              <button className="mt-2 bg-green-600 hover:bg-green-700 text-white font-semibold px-6 py-2 rounded-full transition duration-300 shadow">
                Apply Now
              </button>
            </div>
            <div>
              <p className="text-sm font-semibold text-gray-500 uppercase tracking-wide">CALL US AT</p>
              <a href="tel:+918887141052" className="text-lg font-bold text-green-600 hover:underline">
                +91 88871 41052
              </a>
            </div>
            <div>
              <p className="text-sm font-semibold text-gray-500 uppercase tracking-wide">MAIL US AT</p>
              <a href="mailto:info@cashpro.co.in" className="text-lg font-bold text-green-600 hover:underline">
                info@cashpro.co.in
              </a>
            </div>
          </div>
          <Link to="/contact" className="inline-block text-green-600 hover:underline font-medium">
            Contact Us →
          </Link>
        </div>
      </div>

      <style jsx>{`
        @keyframes fade-up {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-up { animation: fade-up 0.6s ease-out forwards; }
        .animate-fade-in-up { animation: fade-up 0.6s ease-out forwards; }
        .animation-delay-200 { animation-delay: 0.2s; }
        .animation-delay-400 { animation-delay: 0.4s; }
      `}</style>
    </div>
  );
};

export default PersonalLoanBallia;